import { Injectable } from '@angular/core';
import { Permission } from '@app/core/models/permissions.interface';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as Selectors from '@app/modules/features/modules/tasks/store/tasks.selectors';
import {
  RoomCleanTaskStatus,
  Status,
  TaskFilter,
} from '@app/core/async-services/http/versioned/task-management/task-management.interface';
import { State } from '@app/store/store.state';
import { PermissionService } from '@app/core/services/permissions/permission.service';
import { ServiceService } from '@app/core/services/service.service';
import { FeatureFlagService } from '@app/core/services/feature-flag/feature-flag.service';
import { selectUrl } from '@app/store/router/router.selectors';

@Injectable({ providedIn: 'root' })
export class TaskService {
  constructor(
    private _permissionService: PermissionService,
    private _store: Store<State>,
    private _serviceService: ServiceService,
    private featureFlagService: FeatureFlagService,
  ) {}

  get canDeleteTask$(): Observable<boolean> {
    return combineLatest([
      this._permissionService.hasPermission$(
        Permission.CanAssignAndDeleteTasksCreatedByOthers,
      ),
      this._permissionService.hasPermission$(
        Permission.CanCreateTasksAndAssignOrDeleteTasksCreatedByMyself,
      ),
      this._store.select(Selectors.selectIsTaskCreatedBySelf),
    ]).pipe(
      map(
        ([canDeleteAny, hasDeleteOwnTaskPermission, isTaskCreatedBySelf]) =>
          canDeleteAny || (hasDeleteOwnTaskPermission && isTaskCreatedBySelf),
      ),
    );
  }

  get canMoveTaskBackToOpen$(): Observable<boolean> {
    return combineLatest([
      this._store.select(Selectors.selectTaskStatusName),
      this._store.select(Selectors.selectIsTaskAssignedToSelf),
    ]).pipe(
      map(
        ([statusName, isTaskAssignedToSelf]) =>
          statusName === Status.IN_PROGRESS && isTaskAssignedToSelf,
      ),
    );
  }

  get canMoveTaskBackToInProgress$(): Observable<boolean> {
    return combineLatest([
      this._store.select(Selectors.selectTaskStatusName),
      this._store.select(Selectors.selectIsTaskAssignedToSelf),
    ]).pipe(
      map(
        ([statusName, isTaskAssignedToSelf]) =>
          statusName === Status.DONE && isTaskAssignedToSelf,
      ),
    );
  }

  get canChangeStatusToCleaning$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(map((statusName) => statusName !== RoomCleanTaskStatus.CLEANING));
  }

  get canChangeStatusToDirty$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(map((statusName) => statusName === RoomCleanTaskStatus.CLEANING));
  }

  get canChangeStatusToDeclinedService$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(
        map(
          (statusName) =>
            statusName === RoomCleanTaskStatus.DIRTY ||
            statusName === RoomCleanTaskStatus.DND ||
            statusName === Status.OPEN,
        ),
      );
  }

  get canChangeStatusToDND$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(
        map(
          (statusName) =>
            statusName === RoomCleanTaskStatus.DIRTY ||
            statusName === Status.OPEN,
        ),
      );
  }

  get canChangeStatusToDone$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(
        map(
          (statusName) =>
            statusName === RoomCleanTaskStatus.CLEANING ||
            statusName === Status.IN_PROGRESS ||
            statusName === RoomCleanTaskStatus.RETURN_LATER,
        ),
      );
  }

  get canChangeStatusToInspected$(): Observable<boolean> {
    return combineLatest([
      this.featureFlagService.isInspectedRoomStatusOn$,
      this._store.select(Selectors.selectTaskStatusName),
      this._store.select(selectUrl),
    ]).pipe(
      map(
        ([isFeatureEnabled, statusName, url]) =>
          isFeatureEnabled &&
          statusName === RoomCleanTaskStatus.DONE &&
          url.includes('realtime-rooms'),
      ),
    );
  }

  get canChangeStatusToReturnLater$(): Observable<boolean> {
    return this._store
      .select(Selectors.selectTaskStatusName)
      .pipe(
        map(
          (statusName) =>
            statusName === RoomCleanTaskStatus.DECLINED_SERVICE ||
            statusName === RoomCleanTaskStatus.DIRTY ||
            statusName === RoomCleanTaskStatus.DND ||
            statusName === Status.OPEN,
        ),
      );
  }

  getFilterOptions(entId: string): TaskFilter[] {
    const canAccessAndPickUpTasks = this._permissionService
      .doesUserHavePermission(`(
      ${Permission.CanAccessAndPickUpTasks}
    )`);
    const canAccessBoardBuilderV2AndRoomCleanTasks = this._permissionService
      .doesUserHavePermission(`(
      ${Permission.CanAccessBoardBuilderV2AndRoomCleanTasks}
    )`);
    const hasRealtimeRoomsService =
      this._serviceService.doesHotelHaveRealtimeRoomsService(entId);
    const hasTaskManagementService =
      this._serviceService.doesHotelHaveTaskManagementService(entId);
    const filterOptions: TaskFilter[] = [];
    if (canAccessAndPickUpTasks && hasTaskManagementService) {
      filterOptions.push(
        TaskFilter.OPEN_TASKS,
        TaskFilter.UNFINISHED_GUEST_REQUESTS,
        TaskFilter.UNASSIGNED_OPEN_TASKS,
        TaskFilter.ASSIGNED_UNFINISHED_TASKS,
        TaskFilter.RECENTLY_FINISHED_TASKS,
      );
    }
    if (canAccessBoardBuilderV2AndRoomCleanTasks && hasRealtimeRoomsService) {
      filterOptions.push(
        TaskFilter.MY_UNFINISHED_ROOM_CLEAN_TASKS,
        TaskFilter.MY_RECENTLY_FINISHED_ROOM_CLEAN_TASKS,
      );
    }
    return filterOptions;
  }
}
